import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, InlineNotification, Modal, Select, SelectItem, Tab, TabList, TabPanel, TabPanels, Tabs, Tag, TextArea, TextInput, Toggle } from '@carbon/react';
import { ArrowDown, ArrowUp, TrashCan } from '@carbon/icons-react';
import { GAME_BY_ID, GAME_CATALOG, LIMIT_GAMES, fromZoneInput, toZoneInput } from '@ruby-cms/shared';
import { api, ApiError } from '../api/client';
import { BlockSkeleton, ErrorState, InlineError, PageHeader } from '../components/states';
import { When } from '../components/format';
import { CommandStatus, HE_A_ERRORS } from '../components/labels';
import type { Command } from './Commands';
import { afterSave, keepDraft, loadDraft, rebase, type Draft, type SaveOutcome } from './contentDraft';
import { NoticePush } from './ContentNotice';
import { ImageField, Thumb, type Images } from '../components/ImageField';

type Sched = { tu?: string; den?: string };
type Action = { kieu: 'game' | 'url' | 'man' | 'none'; gia?: string };
type Banner = Sched & { anh: string; ten?: string; hanhDong: Action };
type Line = Sched & { text: string; giay: number };
type Popup = Sched & { ma: string; noiDung: string; anh?: string; lapLaiGio: number };
type Doc = { chuChay?: Line[]; banner?: Banner[]; lienKet?: Record<string, string>; popupTelegram?: { bat: boolean }; popup?: Popup[]; trangChu?: unknown; giaoDien?: Record<string, boolean>; sanhLive?: Record<string, boolean>; thuTu?: string[] };
// seasonal lobby skins the client knows (/arik flags); he A fills every flag it serves, so the page shows what players see
const SKINS: [string, string][] = [['activeMidAutumnFestival', 'Trung Thu'], ['isNewYearSkin', 'Tết'], ['activeThemeChristmas', 'Giáng sinh (Noel)'], ['activeThemeWorldCup', 'World Cup']];
type Data = { noiDung: Doc; meta: { version: number; source: string; at: string | null }; hiddenGames: string[]; lobbies?: { id: string; ten: string; macDinh?: boolean }[]; autoOrder?: string[]; pending: { id: string; status: string }[] };

const REPEAT: [number, string][] = [[0, 'Mỗi lần vào game'], [24, 'Tối đa 1 lần mỗi 24 giờ'], [72, 'Tối đa 1 lần mỗi 3 ngày'], [168, 'Tối đa 1 lần mỗi 7 ngày'], [720, 'Tối đa 1 lần mỗi 30 ngày']];

const LINK: { key: string; label: string }[] = [
  { key: 'hoTroTelegram', label: 'Telegram hỗ trợ' },
  { key: 'telegramThongBao', label: 'Telegram thông báo' },
  { key: 'liveChat', label: 'Live chat' },
  { key: 'liveChatVip', label: 'Live chat VIP' },
  { key: 'hotline', label: 'Hotline' },
  { key: 'fanpage', label: 'Fanpage (để trống)' },
  { key: 'congDong', label: 'Cộng đồng (để trống)' },
];

// lobby screens a banner can open (backend MAN_BANNER, client BannerActionHandler)
const SCREENS: [string, string][] = [
  ['naprut', 'Nạp / rút'], ['giftcode', 'Giftcode'], ['nhiemvuhangngay', 'Nhiệm vụ hằng ngày'], ['sukien', 'Sự kiện'], ['mailbox', 'Hộp thư'],
  ['news', 'Tin tức'], ['support247', 'Hỗ trợ 24/7'], ['sanhu', 'Săn hũ'], ['lichsucuoc', 'Lịch sử cược'], ['userinfo', 'Thông tin tài khoản'], ['duatoptx', 'Đua top Tài Xỉu'],
];
// games a banner can open: those with a running engine (every one takes stakes, so it has a limit row)
const PLAYABLE = new Set(LIMIT_GAMES.map((l) => l.id));
const GAMES = GAME_CATALOG.filter((g) => PLAYABLE.has(g.id));

const SAVE_ERR: Record<string, string> = { PHIEN_BAN_CU: 'Nội dung sảnh vừa được người khác lưu', SAI_KIEU: 'Hệ A từ chối nội dung' };

// datetime-local <-> ISO (the operator types the time of the CMS time zone)
const toLocal = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : toZoneInput(d);
};
const fromLocal = (v: string) => (v ? fromZoneInput(v)?.toISOString() : undefined);
const liveAt = (s: Sched, t: number) => (!s.tu || Date.parse(s.tu) <= t) && (!s.den || t < Date.parse(s.den));

function SchedTag({ s, at }: { s: Sched; at: number }) {
  if (s.den && Date.parse(s.den) <= at) return <Tag size="sm" type="gray">Đã hết hạn</Tag>;
  if (s.tu && Date.parse(s.tu) > at) return <Tag size="sm" type="blue">Chưa tới giờ</Tag>;
  return <Tag size="sm" type="green">Đang hiện</Tag>;
}

function SchedFields({ id, s, onChange }: { id: string; s: Sched; onChange: (s: Sched) => void }) {
  return (
    <>
      <TextInput id={id + '-tu'} type="datetime-local" labelText="Hiện từ (trống = ngay)" value={toLocal(s.tu)} onChange={(e) => onChange({ ...s, tu: fromLocal(e.target.value) })} />
      <TextInput id={id + '-den'} type="datetime-local" labelText="Hiện đến (trống = không hết)" value={toLocal(s.den)} onChange={(e) => onChange({ ...s, den: fromLocal(e.target.value) })} />
    </>
  );
}

// the client's marquee markup: <color=#hex>text</c>
function Marquee({ text }: { text: string }) {
  const parts: { t: string; c?: string }[] = [];
  const re = /<color=(#[0-9a-fA-F]{3,8})>(.*?)<\/c(?:olor)?>/g;
  let i = 0;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    if (m.index > i) parts.push({ t: text.slice(i, m.index) });
    parts.push({ t: m[2] ?? '', c: m[1] });
    i = m.index + m[0].length;
  }
  if (i < text.length) parts.push({ t: text.slice(i) });
  return <span>{parts.map((p, k) => <span key={k} style={p.c ? { color: p.c } : undefined}>{p.t}</span>)}</span>;
}

function ActionFields({ id, a, onChange }: { id: string; a: Action; onChange: (a: Action) => void }) {
  return (
    <>
      <Select id={id + '-k'} labelText="Bấm vào banner" value={a.kieu} onChange={(e) => {
        const kieu = e.target.value as Action['kieu'];
        onChange(kieu === 'none' ? { kieu } : { kieu, gia: kieu === 'man' ? 'sukien' : kieu === 'game' ? GAMES[0]?.id : 'https://' });
      }}>
        <SelectItem value="none" text="Không làm gì" />
        <SelectItem value="game" text="Mở game" />
        <SelectItem value="man" text="Mở màn hình sảnh" />
        <SelectItem value="url" text="Mở liên kết" />
      </Select>
      {a.kieu === 'game' ? (
        <Select id={id + '-g'} labelText="Game" value={a.gia ?? ''} onChange={(e) => onChange({ kieu: 'game', gia: e.target.value })}>
          {GAMES.map((g) => <SelectItem key={g.id} value={g.id} text={`${g.name} (${g.id})`} />)}
        </Select>
      ) : a.kieu === 'man' ? (
        <Select id={id + '-m'} labelText="Màn hình" value={a.gia ?? ''} onChange={(e) => onChange({ kieu: 'man', gia: e.target.value })}>
          {SCREENS.map(([v, t]) => <SelectItem key={v} value={v} text={t} />)}
        </Select>
      ) : a.kieu === 'url' ? (
        <TextInput id={id + '-u'} labelText="Liên kết (https://)" value={a.gia ?? ''} onChange={(e) => onChange({ kieu: 'url', gia: e.target.value.trim() })} />
      ) : null}
    </>
  );
}

function move<T>(xs: T[], i: number, d: -1 | 1): T[] {
  const j = i + d;
  if (j < 0 || j >= xs.length) return xs;
  const out = [...xs];
  [out[i], out[j]] = [out[j]!, out[i]!];
  return out;
}

const SANH = 'ruby-sanh:';

// order number of one lobby tile: typed freely, applied on Enter or when the field loses focus
function OrderNumber({ id, n, max, onMove }: { id: string; n: number; max: number; onMove: (to: number) => void }) {
  const [v, setV] = useState(String(n));
  useEffect(() => setV(String(n)), [n]);
  const apply = () => {
    const to = Number(v);
    if (Number.isInteger(to) && to >= 1 && to !== n) onMove(Math.min(to, max));
    else setV(String(n));
  };
  return (
    <div style={{ flex: '0 0 72px' }}>
      <TextInput id={id} size="sm" type="number" min={1} max={max} labelText="Thứ tự" hideLabel value={v}
        onChange={(e) => setV(e.target.value)} onBlur={apply} onKeyDown={(e) => { if (e.key === 'Enter') apply(); }} />
    </div>
  );
}

export function ContentPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['content'], queryFn: () => api.get<Data>('/api/admin/content') });
  const images = useQuery({ queryKey: ['banners'], queryFn: () => api.get<Images>('/api/admin/content/banners') });
  // the draft keeps the version it was built on (taken at the first edit): a refetch must not move
  // it, or the save would pass the version check and overwrite what another operator saved.
  // It is stored in the browser too, and only a save he A accepted clears it.
  const [draft, setDraftState] = useState<Draft<Doc> | null>(() => loadDraft<Doc>());
  const setDraft = (d: Draft<Doc> | null) => { setDraftState(d); keepDraft(d); };
  const live = draft?.doc ?? q.data?.noiDung ?? {};
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState<{ kind: 'success' | 'warning' | 'error'; title: string; subtitle: string } | null>(null);
  const [stale, setStale] = useState<number | null>(null);
  const [at, setAt] = useState('');
  const atMs = at ? fromZoneInput(at)?.getTime() ?? Date.now() : Date.now();

  const refused = (o: SaveOutcome, title: string, subtitle: string) => {
    setDraft(afterSave(draft, o));
    setOpen(false);
    setNotice({ kind: 'error', title, subtitle: subtitle + ' Bản nháp của bạn vẫn giữ nguyên.' });
    // after a stale refusal the page shows the server's newer version next to the kept draft: wait
    // for the refetch, the cached version is the one the save was refused against
    if (o.kind === 'stale') { setStale(-1); void q.refetch().then((r) => { if (r.data) setStale(r.data.meta.version); }); }
  };
  const save = useMutation({
    mutationFn: () => api.put<{ command: Command }>('/api/admin/content', { noiDung: live, expect: draft?.version ?? q.data?.meta.version ?? 0 }),
    onSuccess: (r) => {
      const c = r.command;
      if (c.status === 'SUCCEEDED') {
        setDraft(afterSave(draft, { kind: 'saved' })); setOpen(false); setStale(null);
        qc.invalidateQueries({ queryKey: ['content'] });
        setNotice({ kind: 'success', title: 'Hệ A đã nhận nội dung mới', subtitle: 'Người chơi thấy khi tải lại sảnh.' });
        return;
      }
      if (c.status === 'FAILED' && c.lastError === 'PHIEN_BAN_CU') {
        refused({ kind: 'stale', serverVersion: -1 }, SAVE_ERR.PHIEN_BAN_CU!, 'Trên máy chủ đã có bản mới hơn, thay đổi của bạn chưa được lưu.');
        return;
      }
      const code = c.lastError ?? c.status;
      refused({ kind: c.status === 'FAILED' ? 'refused' : 'pending', code }, c.status === 'FAILED' ? (SAVE_ERR[code] ?? 'Hệ A từ chối lưu') : 'Lệnh chưa xong',
        (HE_A_ERRORS[code] ?? code) + '.');
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409 && e.code === 'STALE') {
        refused({ kind: 'stale', serverVersion: -1 }, SAVE_ERR.PHIEN_BAN_CU!, `Bản bạn sửa dựa trên phiên bản ${draft?.version ?? '?'}, trên máy chủ đã có bản mới hơn.`);
        return;
      }
      const code = e instanceof ApiError ? e.code : 'NETWORK';
      refused({ kind: 'refused', code }, 'Chưa lưu được', e instanceof Error ? e.message + '.' : String(e));
    },
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const toiDa = images.data?.toiDa;
      if (!/^image\/(png|jpeg|webp)$/.test(file.type)) throw new ApiError(400, 'KHONG_PHAI_ANH', 'Chỉ nhận ảnh PNG, JPG hoặc WEBP');
      if (toiDa && file.size > toiDa) throw new ApiError(400, 'ANH_QUA_LON', `Ảnh ${Math.ceil(file.size / 1024)} KB, tối đa ${Math.floor(toiDa / 1024)} KB`);
      return api.upload<{ anh: string; bytes: number }>('/api/admin/content/banners', file);
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['banners'] });
      set({ banner: [...(live.banner ?? []), { anh: r.anh, hanhDong: { kieu: 'none' } }] });
    },
  });
  const set = (p: Partial<Doc>) => setDraft({ doc: { ...live, ...p }, version: draft?.version ?? q.data?.meta.version ?? 0, savedAt: Date.now() });
  const setBanner = (i: number, b: Banner) => { const banner = [...(live.banner ?? [])]; banner[i] = b; set({ banner }); };
  const setLine = (i: number, m: Line) => { const chuChay = [...(live.chuChay ?? [])]; chuChay[i] = m; set({ chuChay }); };

  // what the lobby shows: in its window, not opening a hidden game, at most soBannerToiDa of them
  const hidden = useMemo(() => new Set(q.data?.hiddenGames ?? []), [q.data?.hiddenGames]);
  const cap = images.data?.soBannerToiDa ?? 8;
  const shown = (b: Banner) => liveAt(b, atMs) && !(b.hanhDong.kieu === 'game' && hidden.has(b.hanhDong.gia ?? ''));
  const liveBanners = useMemo(() => (live.banner ?? []).filter(shown).slice(0, cap), [live.banner, atMs, hidden, cap]);
  const bannerNote = (b: Banner) => (b.hanhDong.kieu === 'game' && hidden.has(b.hanhDong.gia ?? '') ? 'Game này đang ẩn: banner không hiện'
    : shown(b) && !liveBanners.includes(b) ? `Sảnh chỉ hiện ${cap} banner đầu: banner này không hiện` : null);
  const setPopup = (i: number, p: Popup) => { const popup = [...(live.popup ?? [])]; popup[i] = p; set({ popup }); };
  const livePopups = (live.popup ?? []).filter((p) => liveAt(p, atMs));
  const liveLines = useMemo(() => (live.chuChay ?? []).filter((m) => liveAt(m, atMs)), [live.chuChay, atMs]);
  // lobby order: the saved list (games he A no longer lists dropped, new ones at the end, where the client puts
  // them), or the order players see now while none is saved
  const autoOrder = q.data?.autoOrder;
  const order = useMemo(() => {
    const auto = autoOrder ?? [];
    const saved = (live.thuTu ?? []).filter((id) => auto.includes(id));
    return saved.length ? [...saved, ...auto.filter((id) => !saved.includes(id))] : auto;
  }, [live.thuTu, autoOrder]);
  const moveTo = (i: number, to: number) => {
    const out = [...order];
    out.splice(to, 0, ...out.splice(i, 1));
    set({ thuTu: out });
  };
  const tileName = (id: string) => (id.startsWith(SANH)
    ? `Sảnh ${q.data?.lobbies?.find((l) => SANH + l.id === id)?.ten ?? id.slice(SANH.length)}`
    : GAME_BY_ID.get(id)?.name ?? id);
  // the sanh chua tung luu tren CMS: theo mac dinh cua no (SABA tat cho toi khi bat)
  const lobbyOn = (sid: string) => live.sanhLive?.[sid] ?? (q.data?.lobbies?.find((l) => l.id === sid)?.macDinh !== false);
  const tileOff = (id: string) => (id.startsWith(SANH) ? (!lobbyOn(id.slice(SANH.length)) ? 'Đang tắt' : null) : hidden.has(id) ? 'Đang ẩn' : null);

  return (
    <div className="page">
      <PageHeader title="Nội dung sảnh" lead="Banner, chữ chạy, liên kết và giao diện sảnh, có lịch hiện và xem trước. Người chơi thấy bản mới khi tải lại sảnh; thông báo gửi ngay tới người đang online." />
      {q.isLoading ? <BlockSkeleton lines={8} /> : q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : (
        <>
          <p className="muted">Phiên bản {q.data?.meta.version} ({q.data?.meta.source === 'cms' ? 'CMS' : 'mặc định'}){q.data?.meta.at ? <> · <When v={q.data.meta.at} /></> : null}
            {draft ? <> · <Tag size="sm" type="magenta">Bản nháp chưa lưu</Tag> dựa trên phiên bản {draft.version}</> : null}</p>
          {notice ? <InlineNotification kind={notice.kind} lowContrast title={notice.title} subtitle={notice.subtitle} onClose={() => setNotice(null)} /> : null}
          {stale !== null && stale >= 0 && draft ? (
            <div className="row" style={{ margin: '8px 0 16px' }}>
              <span>Máy chủ đang ở phiên bản {stale}.</span>
              <Button size="sm" kind="danger--tertiary" onClick={() => { setDraft(rebase(draft, stale)); setStale(null); setNotice(null); }}>Dùng bản nháp của tôi, ghi đè khi lưu</Button>
              <Button size="sm" kind="ghost" onClick={() => { setDraft(null); setStale(null); setNotice(null); }}>Bỏ bản nháp, xem bản mới</Button>
            </div>
          ) : null}
          {q.data?.pending.length ? <p>Có lệnh đang chờ: {q.data.pending.map((p) => <CommandStatus key={p.id} s={p.status} />)}</p> : null}
          <div className="row" style={{ alignItems: 'flex-end', marginBottom: 8 }}>
            <TextInput id="xem-luc" type="datetime-local" labelText="Xem trước như lúc (trống = bây giờ)" value={at} onChange={(e) => setAt(e.target.value)} />
            {at ? <Button kind="ghost" size="sm" onClick={() => setAt('')}>Về bây giờ</Button> : null}
          </div>
          <Tabs>
            <TabList aria-label="Nội dung"><Tab>Banner</Tab><Tab>Chữ chạy</Tab><Tab>Popup vào game</Tab><Tab>Liên kết</Tab><Tab>Giao diện sảnh</Tab><Tab>Gửi thông báo</Tab></TabList>
            <TabPanels>
              <TabPanel>
                <div className="stack">
                  <section className="section section--flush">
                    <h2>Người chơi đang thấy ({liveBanners.length} banner, theo thứ tự)</h2>
                    {liveBanners.length ? (
                      <div className="strip">{liveBanners.map((b, i) => <figure key={i}><Thumb anh={b.anh} wide /><figcaption>{b.ten || b.anh}</figcaption></figure>)}</div>
                    ) : <p className="muted">Không có banner nào hiện vào lúc này.</p>}
                  </section>
                  <div className="row" style={{ alignItems: 'center' }}>
                    <label className="cds--btn cds--btn--tertiary cds--btn--sm" htmlFor="up-anh">Tải ảnh lên và thêm banner</label>
                    <input id="up-anh" className="visually-hidden" type="file" accept="image/png,image/jpeg,image/webp" disabled={upload.isPending}
                      onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) { upload.reset(); upload.mutate(f); } }} />
                    <span className="muted">PNG, JPG, WEBP tối đa {images.data ? Math.floor(images.data.toiDa / 1024) + ' KB' : '...'}{upload.isPending ? ' · đang tải lên' : ''}</span>
                  </div>
                  <InlineError error={upload.error} />
                  {(live.banner ?? []).map((b, i) => (
                    <div className="section card" key={i}>
                      <div className="row row--top">
                        <Thumb anh={b.anh} />
                        <div className="stack grow">
                          <div className="row" style={{ alignItems: 'center' }}>
                            <strong>#{i + 1}</strong> <SchedTag s={b} at={atMs} />
                            {bannerNote(b) ? <Tag size="sm" type="warm-gray">{bannerNote(b)}</Tag> : null}
                            <Button kind="ghost" size="sm" hasIconOnly iconDescription="Lên" renderIcon={ArrowUp} disabled={i === 0} onClick={() => set({ banner: move(live.banner ?? [], i, -1) })} />
                            <Button kind="ghost" size="sm" hasIconOnly iconDescription="Xuống" renderIcon={ArrowDown} disabled={i === (live.banner ?? []).length - 1} onClick={() => set({ banner: move(live.banner ?? [], i, 1) })} />
                            <Button kind="ghost" size="sm" renderIcon={TrashCan} onClick={() => set({ banner: (live.banner ?? []).filter((_, j) => j !== i) })}>Xóa</Button>
                          </div>
                          <div className="row">
                            <Select id={'ba-' + i} labelText="Ảnh" value={b.anh} onChange={(e) => setBanner(i, { ...b, anh: e.target.value })}>
                              {[...(images.data?.anh ?? []).map((a) => a.anh), ...((images.data?.anh ?? []).some((a) => a.anh === b.anh) ? [] : [b.anh])].map((a) => <SelectItem key={a} value={a} text={a} />)}
                            </Select>
                            <TextInput id={'bt-' + i} labelText="Tên (chỉ để quản lý)" value={b.ten ?? ''} onChange={(e) => setBanner(i, { ...b, ten: e.target.value || undefined })} />
                          </div>
                          <div className="row"><ActionFields id={'bh-' + i} a={b.hanhDong} onChange={(hanhDong) => setBanner(i, { ...b, hanhDong })} /></div>
                          <div className="row"><SchedFields id={'bs-' + i} s={b} onChange={(s) => setBanner(i, { ...b, ...s })} /></div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {images.data?.anh.length ? (
                    <details className="section">
                      <summary>Kho ảnh ({images.data.anh.length})</summary>
                      <div className="gallery">{images.data.anh.map((a) => (
                        <button key={a.anh} type="button" className="gallery__item" title="Thêm banner với ảnh này"
                          onClick={() => set({ banner: [...(live.banner ?? []), { anh: a.anh, hanhDong: { kieu: 'none' } }] })}>
                          <Thumb anh={a.anh} /><span>{Math.ceil(a.bytes / 1024)} KB · {a.nguon === 'cms' ? 'tải lên' : 'có sẵn'}</span>
                        </button>
                      ))}</div>
                    </details>
                  ) : null}
                </div>
              </TabPanel>
              <TabPanel>
                <div className="stack">
                  <section className="section section--flush">
                    <h2>Xem trước dải chữ chạy</h2>
                    {liveLines.length ? <div className="marquee" aria-label="Chữ chạy">{liveLines.map((m, i) => <span key={i}><Marquee text={m.text} /></span>)}</div>
                      : <p className="muted">Không có dòng nào chạy vào lúc này.</p>}
                  </section>
                  {(live.chuChay ?? []).map((m, i) => (
                    <div className="section card" key={i}>
                      <div className="row" style={{ alignItems: 'center' }}>
                        <strong>#{i + 1}</strong> <SchedTag s={m} at={atMs} />
                        <Button kind="ghost" size="sm" hasIconOnly iconDescription="Lên" renderIcon={ArrowUp} disabled={i === 0} onClick={() => set({ chuChay: move(live.chuChay ?? [], i, -1) })} />
                        <Button kind="ghost" size="sm" hasIconOnly iconDescription="Xuống" renderIcon={ArrowDown} disabled={i === (live.chuChay ?? []).length - 1} onClick={() => set({ chuChay: move(live.chuChay ?? [], i, 1) })} />
                        <Button kind="ghost" size="sm" renderIcon={TrashCan} onClick={() => set({ chuChay: (live.chuChay ?? []).filter((_, j) => j !== i) })}>Xóa</Button>
                      </div>
                      <div className="row">
                        <TextInput id={'cc-' + i} labelText="Nội dung (màu: <color=#f5dd42>chữ</c>)" value={m.text} onChange={(e) => setLine(i, { ...m, text: e.target.value })} />
                        <TextInput id={'ccg-' + i} labelText="Giây" type="number" value={String(m.giay)} onChange={(e) => setLine(i, { ...m, giay: Number(e.target.value) || 10 })} />
                      </div>
                      <div className="row"><SchedFields id={'cs-' + i} s={m} onChange={(s) => setLine(i, { ...m, ...s })} /></div>
                    </div>
                  ))}
                  <Button kind="tertiary" size="sm" onClick={() => set({ chuChay: [...(live.chuChay ?? []), { text: 'Ruby.club', giay: 10 }] })}>Thêm dòng</Button>
                </div>
              </TabPanel>
              <TabPanel>
                <div className="split">
                  <div className="stack">
                    <p className="muted">Popup hiện giữa màn hình khi người chơi vào sảnh (sau khi đăng nhập), nút OK để đóng. Popup có ảnh hiện nguyên tấm ảnh, bấm ra ngoài hoặc nút X để đóng. Mỗi lần vào game hiện tối đa một popup: popup đầu tiên trong danh sách đang tới lượt người đó.</p>
                    {(live.popup ?? []).map((p, i) => (
                      <div className="section card" key={p.ma}>
                        <div className="row" style={{ alignItems: 'center' }}>
                          <strong>#{i + 1}</strong> <SchedTag s={p} at={atMs} />
                          <Button kind="ghost" size="sm" hasIconOnly iconDescription="Lên" renderIcon={ArrowUp} disabled={i === 0} onClick={() => set({ popup: move(live.popup ?? [], i, -1) })} />
                          <Button kind="ghost" size="sm" hasIconOnly iconDescription="Xuống" renderIcon={ArrowDown} disabled={i === (live.popup ?? []).length - 1} onClick={() => set({ popup: move(live.popup ?? [], i, 1) })} />
                          <Button kind="ghost" size="sm" renderIcon={TrashCan} onClick={() => set({ popup: (live.popup ?? []).filter((_, j) => j !== i) })}>Xóa</Button>
                        </div>
                        <ImageField id={'pa-' + i} value={p.anh} optional onChange={(anh) => setPopup(i, { ...p, anh })} />
                        <TextArea id={'pu-' + i} labelText={p.anh ? `Chữ thay thế cho ảnh, để trống được (${p.noiDung.length}/500)` : `Nội dung (${p.noiDung.length}/500)`} rows={p.anh ? 1 : 3} value={p.noiDung} onChange={(e) => setPopup(i, { ...p, noiDung: e.target.value })} />
                        <div className="row">
                          <Select id={'pl-' + i} labelText="Lặp lại" value={String(p.lapLaiGio)} onChange={(e) => setPopup(i, { ...p, lapLaiGio: Number(e.target.value) })}>
                            {REPEAT.some(([h]) => h === p.lapLaiGio) ? null : <SelectItem value={String(p.lapLaiGio)} text={`Tối đa 1 lần mỗi ${p.lapLaiGio} giờ`} />}
                            {REPEAT.map(([h, t]) => <SelectItem key={h} value={String(h)} text={t} />)}
                          </Select>
                          <SchedFields id={'ps-' + i} s={p} onChange={(s) => setPopup(i, { ...p, ...s })} />
                        </div>
                      </div>
                    ))}
                    {(live.popup ?? []).length < 10 ? (
                      <Button kind="tertiary" size="sm" onClick={() => set({ popup: [...(live.popup ?? []), { ma: crypto.randomUUID().slice(0, 12), noiDung: '', lapLaiGio: 24 }] })}>Thêm popup</Button>
                    ) : null}
                  </div>
                  <section className="client-preview" aria-label="Xem trước popup">
                    <p className="muted">Người chơi vào game lúc này thấy</p>
                    {livePopups[0]?.anh ? <Thumb anh={livePopups[0].anh} wide />
                      : livePopups[0] ? <div className="client-popup"><h3>Thông báo</h3><p>{livePopups[0].noiDung}</p><span className="client-popup__ok">OK</span></div>
                      : <p className="muted">Không có popup nào đang trong lịch.</p>}
                  </section>
                </div>
              </TabPanel>
              <TabPanel>
                <div className="stack" style={{ maxWidth: 520 }}>
                  {LINK.map((l) => (
                    <TextInput key={l.key} id={l.key} labelText={l.label} value={live.lienKet?.[l.key] ?? ''} onChange={(e) => {
                      set({ lienKet: { ...(live.lienKet ?? {}), [l.key]: e.target.value } });
                    }} />
                  ))}
                  <Toggle id="tg-pop" labelText="Hiện popup Telegram khi vào sảnh" toggled={!!live.popupTelegram?.bat} onToggle={(v) => set({ popupTelegram: { bat: v } })} />
                </div>
              </TabPanel>
              <TabPanel>
                <div className="stack" style={{ maxWidth: 520 }}>
                  <p className="muted">Giao diện theo mùa của sảnh game (hình nền, hiệu ứng, nhạc). Người chơi thấy khi tải lại sảnh sau khi lưu.</p>
                  {SKINS.map(([k, label]) => (
                    <Toggle key={k} id={'gd-' + k} labelText={label} labelA="Tắt" labelB="Bật" toggled={!!live.giaoDien?.[k]}
                      onToggle={(v) => set({ giaoDien: { ...(live.giaoDien ?? {}), [k]: v } })} />
                  ))}
                  <Toggle id="gd-hienChuTrungThu" labelText='Chữ "2026 TRUNG THU" trên sảnh' labelA="Ẩn" labelB="Hiện" toggled={live.giaoDien?.hienChuTrungThu !== false}
                    onToggle={(v) => set({ giaoDien: { ...(live.giaoDien ?? {}), hienChuTrungThu: v } })} />
                  <p className="muted">Chỉ ảnh hưởng chữ tiêu đề của giao diện Trung Thu; các hình, hiệu ứng và nhạc khác của mùa giữ nguyên. Khi ẩn, sảnh chỉ mất dòng chữ đó.</p>
                  <Toggle id="gd-hienChuWorldCup" labelText="Hiện chữ WORLD CUP 2026" labelA="Ẩn" labelB="Hiện" toggled={!!live.giaoDien?.hienChuWorldCup}
                    onToggle={(v) => set({ giaoDien: { ...(live.giaoDien ?? {}), hienChuWorldCup: v } })} />
                  <p className="muted">Chỉ ảnh hưởng chữ tiêu đề của giao diện World Cup; các hình, hiệu ứng và nhạc khác của mùa giữ nguyên. Khi ẩn, sảnh chỉ mất dòng chữ đó.</p>
                  {SKINS.filter(([k]) => live.giaoDien?.[k]).length > 1 ? <p className="muted">Đang bật nhiều giao diện cùng lúc: sảnh hiện chồng hình của các mùa, nhạc chỉ phát một mùa.</p> : null}
                  <h4 style={{ marginTop: 16 }}>Sảnh live casino và thể thao</h4>
                  <p className="muted">Tắt một sảnh: thẻ sảnh biến khỏi mọi tab của sảnh game khi người chơi tải lại, và hệ game từ chối mở sảnh đó (kể cả link cũ). Bật lại: thẻ về đúng chỗ cũ.</p>
                  {(q.data?.lobbies ?? []).map((l) => (
                    <Toggle key={l.id} id={'sl-' + l.id} labelText={l.ten} labelA="Tắt" labelB="Bật" toggled={lobbyOn(l.id)}
                      onToggle={(v) => set({ sanhLive: { ...(live.sanhLive ?? {}), [l.id]: v } })} />
                  ))}
                  {order.length ? (
                    <>
                      <h4 style={{ marginTop: 16 }}>Thứ tự game và sảnh</h4>
                      <p className="muted">Số nhỏ đứng trước (trái sang phải). Mọi tab của sảnh game xếp theo thứ tự này khi người chơi tải lại; game đang ẩn và sảnh đang tắt vẫn không hiện. {live.thuTu?.length
                        ? 'Đang dùng thứ tự đã đặt.'
                        : 'Chưa đặt thứ tự: sảnh tự xen kẽ thẻ sảnh live giữa các game, các số dưới đây là thứ tự người chơi đang thấy.'}</p>
                      {live.thuTu?.length ? <div><Button kind="ghost" size="sm" onClick={() => set({ thuTu: [] })}>Về thứ tự tự động</Button></div> : null}
                      <ol className="list" aria-label="Thứ tự game và sảnh" style={{ listStyle: 'none', padding: 0 }}>
                        {order.map((id, i) => (
                          <li key={id} className="row" style={{ alignItems: 'center', gap: 8 }} data-tile={id}>
                            <OrderNumber id={'tt-' + id} n={i + 1} max={order.length} onMove={(to) => moveTo(i, to - 1)} />
                            <span style={{ flex: 1 }}>{tileName(id)} <span className="muted num">{id.startsWith(SANH) ? '' : id}</span></span>
                            {tileOff(id) ? <Tag size="sm" type="gray">{tileOff(id)}</Tag> : null}
                            <Button kind="ghost" size="sm" hasIconOnly iconDescription="Lên" renderIcon={ArrowUp} disabled={i === 0} onClick={() => moveTo(i, i - 1)} />
                            <Button kind="ghost" size="sm" hasIconOnly iconDescription="Xuống" renderIcon={ArrowDown} disabled={i === order.length - 1} onClick={() => moveTo(i, i + 1)} />
                          </li>
                        ))}
                      </ol>
                    </>
                  ) : null}
                </div>
              </TabPanel>
              <TabPanel>
                <NoticePush />
              </TabPanel>
            </TabPanels>
          </Tabs>
          <div className="row" style={{ marginTop: 16 }}>
            <Button disabled={!draft || (stale !== null && stale >= 0)} onClick={() => { setNotice(null); save.reset(); setOpen(true); }}>Lưu lên hệ A</Button>
            {draft ? <Button kind="ghost" onClick={() => { setDraft(null); setStale(null); }}>Bỏ thay đổi</Button> : null}
          </div>
        </>
      )}
      <Modal open={open} modalHeading="Lưu nội dung sảnh" primaryButtonText="Gửi" secondaryButtonText="Hủy"
        primaryButtonDisabled={save.isPending} onRequestClose={() => setOpen(false)} onRequestSubmit={() => save.mutate()}>
        <p>{(live.banner ?? []).length} banner, {(live.chuChay ?? []).length} dòng chữ chạy, {(live.popup ?? []).length} popup vào game. Bản nháp dựa trên phiên bản {draft?.version ?? q.data?.meta.version}.</p>
        <InlineError error={save.error} />
      </Modal>
    </div>
  );
}
